/**
 * Documents Admin View
 *
 * Document knowledge base management: upload, list, search, read, and delete documents.
 */

import { html, nothing } from "lit";

// --------------- Types ---------------

export type DocumentEntry = {
  filename: string;
  projectId: string;
  format: string;
  chunks: number;
  createdAt: string;
  metadata: Record<string, unknown>;
};

export type DocumentSearchResult = {
  filename: string;
  chunkIndex: number;
  totalChunks: number;
  format: string;
  preview: string;
  score: number;
};

export type DocumentsAdminProps = {
  documents: DocumentEntry[];
  loading: boolean;
  uploading: boolean;
  error: string | null;
  filterProjectId: string | null;
  projects: Array<{ id: string; name: string; slug?: string }>;
  searchQuery: string;
  searchResults: DocumentSearchResult[];
  searching: boolean;
  readFilename: string | null;
  readText: string | null;
  readLoading: boolean;
  onRefresh: () => void;
  onUpload: (file: File, projectId: string) => void;
  onSearch: () => void;
  onSearchQueryChange: (query: string) => void;
  onRead: (projectId: string, filename: string) => void;
  onDelete: (projectId: string, filename: string) => void;
  onFilterChange: (projectId: string | null) => void;
  onCloseRead: () => void;
};

// --------------- Format badge color ---------------

function formatColor(format: string): string {
  switch (format.toLowerCase()) {
    case "pdf":
      return "#e74c3c";
    case "docx":
      return "#2980b9";
    case "pptx":
      return "#e67e22";
    case "xlsx":
      return "#27ae60";
    case "image":
      return "#8e44ad";
    case "html":
      return "#f39c12";
    case "latex":
      return "#16a085";
    default:
      return "#7f8c8d";
  }
}

// --------------- Render ---------------

export function renderDocumentsAdmin(props: DocumentsAdminProps) {
  const activeProjectId = props.filterProjectId ?? props.projects[0]?.id ?? "";

  return html`
    <div style="display:flex;flex-direction:column;gap:20px;">

      <!-- Header: project filter + actions -->
      <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px;">
        <div style="display:flex;align-items:center;gap:12px;">
          <label class="field" style="margin:0;min-width:200px;">
            <select
              .value=${props.filterProjectId ?? ""}
              @change=${(e: Event) => {
                const v = (e.target as HTMLSelectElement).value;
                props.onFilterChange(v || null);
              }}
            >
              <option value="">All Projects</option>
              ${props.projects.map((p) => html`<option value=${p.id}>${p.name}</option>`)}
            </select>
          </label>
          <span style="font-size:13px;color:var(--text-muted);">
            ${props.documents.length} document${props.documents.length === 1 ? "" : "s"}
          </span>
        </div>
        <div style="display:flex;gap:8px;">
          <button class="btn" @click=${props.onRefresh} ?disabled=${props.loading}>
            ${props.loading ? "Loading..." : "Refresh"}
          </button>
          <label class="btn primary" style="cursor:pointer;position:relative;">
            ${props.uploading ? "Uploading..." : "Upload Document"}
            <input
              type="file"
              accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.tex,.html,.htm,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.webp,.gif,.txt,.md,.csv"
              style="position:absolute;inset:0;opacity:0;cursor:pointer;"
              ?disabled=${props.uploading || !activeProjectId}
              @change=${(e: Event) => {
                const input = e.target as HTMLInputElement;
                const file = input.files?.[0];
                if (file && activeProjectId) {
                  props.onUpload(file, activeProjectId);
                  input.value = "";
                }
              }}
            />
          </label>
        </div>
      </div>

      ${
        props.error
          ? html`<div class="card" style="border-color:#e74c3c;background:rgba(231,76,60,0.08);padding:12px;font-size:13px;color:#e74c3c;">
            ${props.error}
          </div>`
          : nothing
      }

      <!-- Search bar -->
      <div style="display:flex;gap:8px;">
        <input
          class="input"
          type="text"
          placeholder="Search documents by content..."
          style="flex:1;"
          .value=${props.searchQuery}
          @input=${(e: Event) => props.onSearchQueryChange((e.target as HTMLInputElement).value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") {
              props.onSearch();
            }
          }}
        />
        <button class="btn" @click=${props.onSearch} ?disabled=${props.searching || !props.searchQuery.trim()}>
          ${props.searching ? "Searching..." : "Search"}
        </button>
      </div>

      <!-- Search results -->
      ${
        props.searchResults.length > 0
          ? html`
            <div class="card" style="padding:16px;">
              <h3 style="margin:0 0 12px;font-size:14px;font-weight:600;">
                Search Results (${props.searchResults.length})
              </h3>
              <div style="display:flex;flex-direction:column;gap:10px;">
                ${props.searchResults.map(
                  (r) => html`
                    <div style="border:1px solid var(--border);border-radius:6px;padding:10px;">
                      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
                        <span style="font-weight:600;font-size:13px;">${r.filename}</span>
                        <span style="font-size:11px;color:var(--text-muted);">
                          chunk ${r.chunkIndex + 1}/${r.totalChunks} | ${r.score}% match
                        </span>
                      </div>
                      <p style="margin:0;font-size:12px;color:var(--text-muted);line-height:1.5;white-space:pre-wrap;">${r.preview}</p>
                    </div>
                  `,
                )}
              </div>
            </div>
          `
          : nothing
      }

      <!-- Read panel -->
      ${
        props.readFilename
          ? html`
            <div class="card" style="padding:16px;">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
                <h3 style="margin:0;font-size:14px;font-weight:600;">${props.readFilename}</h3>
                <button class="btn" @click=${props.onCloseRead}>Close</button>
              </div>
              ${
                props.readLoading
                  ? html`
                      <p style="font-size: 13px; color: var(--text-muted)">Loading...</p>
                    `
                  : props.readText
                    ? html`<pre style="margin:0;font-size:12px;line-height:1.5;white-space:pre-wrap;max-height:500px;overflow:auto;background:var(--bg-subtle);padding:12px;border-radius:6px;">${props.readText}</pre>`
                    : html`
                        <p style="font-size: 13px; color: var(--text-muted)">No content.</p>
                      `
              }
            </div>
          `
          : nothing
      }

      <!-- Document list -->
      ${
        props.documents.length === 0 && !props.loading
          ? html`
              <div class="card" style="padding: 40px; text-align: center">
                <p style="font-size: 14px; color: var(--text-muted); margin: 0 0 8px">No documents yet</p>
                <p style="font-size: 12px; color: var(--text-muted); margin: 0">
                  Upload a PDF, DOCX, PPTX, XLSX, or image to build your knowledge base.
                </p>
              </div>
            `
          : html`
            <div class="card" style="overflow:auto;">
              <table style="width:100%;border-collapse:collapse;font-size:13px;">
                <thead>
                  <tr style="border-bottom:2px solid var(--border);">
                    <th style="text-align:left;padding:10px 12px;font-weight:600;">Filename</th>
                    <th style="text-align:left;padding:10px 12px;font-weight:600;">Format</th>
                    <th style="text-align:center;padding:10px 12px;font-weight:600;">Chunks</th>
                    <th style="text-align:left;padding:10px 12px;font-weight:600;">Uploaded</th>
                    <th style="text-align:right;padding:10px 12px;font-weight:600;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  ${props.documents.map(
                    (doc) => html`
                      <tr style="border-bottom:1px solid var(--border);">
                        <td style="padding:10px 12px;font-weight:500;">${doc.filename}</td>
                        <td style="padding:10px 12px;">
                          <span style="
                            display:inline-block;
                            padding:2px 8px;
                            border-radius:4px;
                            font-size:11px;
                            font-weight:600;
                            color:#fff;
                            background:${formatColor(doc.format)};
                          ">${doc.format.toUpperCase()}</span>
                        </td>
                        <td style="padding:10px 12px;text-align:center;">${doc.chunks}</td>
                        <td style="padding:10px 12px;color:var(--text-muted);">
                          ${new Date(doc.createdAt).toLocaleDateString()}
                        </td>
                        <td style="padding:10px 12px;text-align:right;">
                          <div style="display:flex;gap:6px;justify-content:flex-end;">
                            <button
                              class="btn"
                              style="font-size:11px;padding:4px 10px;"
                              @click=${() => props.onRead(doc.projectId, doc.filename)}
                            >Read</button>
                            <button
                              class="btn"
                              style="font-size:11px;padding:4px 10px;color:#e74c3c;"
                              @click=${() => props.onDelete(doc.projectId, doc.filename)}
                            >Delete</button>
                          </div>
                        </td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table>
            </div>
          `
      }

      <!-- Supported formats info -->
      <div style="font-size:11px;color:var(--text-muted);padding:0 4px;">
        Supported formats: PDF, DOCX, PPTX, XLSX, LaTeX, HTML, images (OCR), text, markdown, CSV.
        Documents are extracted, chunked, and embedded for semantic search.
      </div>
    </div>
  `;
}
